class RazorpayService {
  constructor() {
    this.keyId = process.env.REACT_APP_RAZORPAY_KEY_ID;
  }

  /**
   * Load Razorpay script dynamically
   */
  loadScript() {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  /**
   * Initiate Razorpay payment
   * @param {Object} options - Payment options
   * @param {string} options.orderId - Order/Payment ID from backend
   * @param {number} options.amount - Amount in paise (e.g., 1000 = ₹10)
   * @param {string} options.currency - Currency code (default: INR)
   * @param {string} options.customerName - Customer name
   * @param {string} options.customerEmail - Customer email
   * @param {string} options.customerPhone - Customer phone
   * @param {string} options.description - Payment description
   * @param {Function} options.onSuccess - Callback on successful payment
   * @param {Function} options.onError - Callback on payment failure
   */
  async initiatePayment(options) {
    const scriptLoaded = await this.loadScript();

    if (!scriptLoaded) {
      throw new Error("Failed to load Razorpay script");
    }

    if (!window.Razorpay) {
      throw new Error("Razorpay is not loaded");
    }

    const paymentOptions = {
      key: this.keyId,
      amount: options.amount, // Amount in paise
      currency: options.currency || "INR",
      name: "Clinic Management",
      description: options.description || "Payment for subscription",
      order_id: options.orderId, // Order ID from backend
      customer: {
        name: options.customerName,
        email: options.customerEmail,
        contact: options.customerPhone,
      },
      handler: (response) => {
        if (options.onSuccess) {
          options.onSuccess(response);
        }
      },
      prefill: {
        name: options.customerName,
        email: options.customerEmail,
        contact: options.customerPhone,
      },
      theme: {
        color: "#3399cc",
      },
      modal: {
        ondismiss: () => {
          if (options.onError) {
            options.onError("Payment cancelled by user");
          }
        },
      },
    };

    const razorpay = new window.Razorpay(paymentOptions);
    razorpay.on("payment.failed", (response) => {
      if (options.onError) {
        options.onError(response.error.description || "Payment failed");
      }
    });

    razorpay.open();
  }

  /**
   * Verify payment on backend (this should return from your API)
   * @param {Object} paymentData - Payment response data
   * @param {Function} verifyCallback - Callback to verify on backend
   */
  async verifyPayment(paymentData, verifyCallback) {
    try {
      const response = await verifyCallback(paymentData);
      return response;
    } catch (error) {
      throw new Error("Payment verification failed: " + error.message);
    }
  }
}

export default new RazorpayService();
